import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase.js';
import { Logo } from './Logo.js';

interface LayoutProps {
  children: React.ReactNode;
  user?: { email: string } | null;
}

export function Layout({ children, user }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  function navClass(path: string) {
    return `text-sm transition ${
      location.pathname === path
        ? 'text-ink font-semibold'
        : 'text-ink-muted hover:text-ink'
    }`;
  }

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/try', label: 'Try it' },
    { path: '/senders', label: 'Senders' },
    { path: '/team', label: 'Team' },
    { path: '/settings', label: 'Settings' },
    { path: '/pricing', label: 'Pricing' },
  ];

  return (
    <div className="min-h-screen bg-surface-50">
      <nav className="border-b border-surface-200 bg-white px-4 sm:px-6 py-3">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-lg font-bold text-ink">
            <Logo className="h-6 w-6 text-brand" />
            <span>AAS</span>
          </Link>

          {/* Desktop nav */}
          {user && (
            <div className="hidden sm:flex items-center gap-6">
              {navLinks.map((l) => (
                <Link key={l.path} to={l.path} className={navClass(l.path)}>{l.label}</Link>
              ))}
              <span className="text-xs text-ink-light truncate max-w-[140px]">{user.email}</span>
              <button
                onClick={handleLogout}
                className="text-xs text-ink-light hover:text-brand transition"
              >
                Logout
              </button>
            </div>
          )}

          {/* Mobile hamburger */}
          {user && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="sm:hidden p-1 text-ink-muted hover:text-ink transition"
              aria-label="Toggle menu"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Mobile menu */}
        {user && menuOpen && (
          <div className="sm:hidden mt-3 border-t border-surface-100 pt-3 space-y-1">
            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={() => setMenuOpen(false)}
                className={`block px-2 py-2 rounded-lg text-sm transition ${
                  location.pathname === l.path
                    ? 'text-ink font-semibold bg-surface-50'
                    : 'text-ink-muted hover:text-ink hover:bg-surface-50'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="px-2 pt-2 border-t border-surface-100 mt-2 flex items-center justify-between">
              <span className="text-xs text-ink-light truncate">{user.email}</span>
              <button
                onClick={handleLogout}
                className="text-xs text-ink-light hover:text-brand transition"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        {children}
      </main>
    </div>
  );
}
